import Facade from '@/Facade'
import { IModel } from '@/model/IModel'
import GameSignals from '@/signals/GameSignals'
import StateSignals from '@/stateMachine/StateSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { toMajorUnits } from '@/utils/CurrencyUnits'

interface AutoplaySettings {
    singleWin?: number
    balanceLower?: number
    balanceHigher?: number
    balanceLowerEnabled?: boolean
    balanceHigherEnabled?: boolean
    stopOnAnyWin?: boolean
}

interface PreferredAutoplayStopSettings {
    stopOnAnyWin?: boolean
    stopOnWinLimit?: boolean
    stopSingleWinValue?: number | null
    stopOnLossLimit?: boolean
    stopLossValue?: number | null
    stopWinMultiplier?: number | null
    stopLossMultiplier?: number | null
}

export class AutoplayManager {
    private static _instanceSeq = 0
    private _model: IModel
    private _currentState: string
    private _left: number
    private _total: number
    private _autoplaySettings: AutoplaySettings
    private _autoPlayBalance: number
    private _isPaused: boolean
    private _hasProcessedSpinSignalForCurrentRound: boolean
    private readonly _instanceId: number

    public constructor(model: IModel) {
        this._instanceId = ++AutoplayManager._instanceSeq
        this._isPaused = false
        this._hasProcessedSpinSignalForCurrentRound = false
        this._model = model
        this._currentState = ''

        console.log(`[AutoplayManager#${this._instanceId}] ctor`)

        GameSignals.gamePause.connect(this.onGamePause)
        StateSignals.stateChanged.connect(this.onStateChanged)
        GameSignals.startAutoplay.connect(this.onStartAutoplay)
        GameSignals.setAutoplaySettings.connect(this.onSetAutoplaySettings)
        GameSignals.stopAutoplay.connect(this.onStopAutoplay)
        GameSignals.activateScatters.connect(this.onFeatureTriggered)
        ;(GameSignals as any).holdAndWinTriggerCoinWinStart?.connect(this.onFeatureTriggered)

        this._autoplaySettings = {
            singleWin: undefined,
            balanceHigher: undefined,
            balanceLower: undefined,
            balanceLowerEnabled: true,
            balanceHigherEnabled: true,
            stopOnAnyWin: false,
        }
    }

    private onGamePause = () => {
        this._isPaused = true

        // Insufficient balance (and other) popups pause the game — clear autospin UI/state.
        if (Facade.isAuto || this._left > 0 || this._total > 0) {
            GameSignals.stopAutoplay.emit()
            this.resetSettings()
            return
        }

        Facade.isAuto = false
        this._total = 0
        this._left = 0
    }

    private onStateChanged = (state: string) => {
        this._currentState = state

        if (this._currentState === GameStatesEnum.START_ROUND_STATE) {
            console.log(`[AutoplayManager#${this._instanceId}] state=START_ROUND`, {
                isAuto: Facade.isAuto,
                leftBefore: this._left,
                total: this._total,
                processedRound: this._hasProcessedSpinSignalForCurrentRound,
            })
            this._hasProcessedSpinSignalForCurrentRound = false
            this.processAutoplayRoundStart()
        }

        if (this._currentState === GameStatesEnum.STOP_SPIN_STATE) {
            console.log(`[AutoplayManager#${this._instanceId}] state=STOP_SPIN`, {
                isAuto: Facade.isAuto,
                left: this._left,
                total: this._total,
                stopOnAnyWin: this._autoplaySettings.stopOnAnyWin,
                balanceLower: this._autoplaySettings.balanceLower,
                balanceLowerEnabled: this._autoplaySettings.balanceLowerEnabled,
                singleWin: this._autoplaySettings.singleWin,
                sessionBalance: this.getCurrentSessionBalance(),
                autoPlayBalance: this._autoPlayBalance,
            })
            this._hasProcessedSpinSignalForCurrentRound = false

            if (Facade.isAuto && this._left > 0 && this.checkConditions()) {
                GameSignals.stopAutoplay.emit()
                Facade.isAuto = false
                this.resetSettings()
            }
        }
    }

    private processAutoplayRoundStart = () => {
        if (!Facade.isAuto || this._model.roundState.isFreeSpins || this._model.isPromoSpinsRound()) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:skip`, {
                reason: !Facade.isAuto ? 'not-auto' : 'free-spins',
                left: this._left,
                total: this._total,
            })
            return
        }

        if (this._hasProcessedSpinSignalForCurrentRound) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:skip`, {
                reason: 'already-processed-round',
                left: this._left,
                total: this._total,
            })
            return
        }

        this._hasProcessedSpinSignalForCurrentRound = true

        const leftBefore = this._left

        if (this._total > 0) {
            this._left = Math.max(0, this._left - 1)
        }

        console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:decrement`, {
            leftBefore,
            leftAfter: this._left,
            total: this._total,
        })

        if (this._left === 0) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:emit stopAutoplay`)
            GameSignals.stopAutoplay.emit()
            return
        }

        console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:emit updateAutoplayValue`, {
            value: this._left,
        })
        GameSignals.updateAutoplayValue.emit(this._left)
    }

    private onStartAutoplay = (value: number) => {
        // In-game Auto Spin only emits startAutoplay — re-apply the last Settings stop prefs.
        this.applyPreferredStopSettingsFromModel()

        Facade.isAuto = true
        this._hasProcessedSpinSignalForCurrentRound = false

        const normalizedValue = Number(value)
        this._total = Number.isFinite(normalizedValue) && normalizedValue > 0 ? Math.floor(normalizedValue) : 0
        this._left = this._total
        this._autoPlayBalance = Number(this._model.balance)

        console.log(`[AutoplayManager#${this._instanceId}] onStartAutoplay`, {
            requested: value,
            normalized: this._total,
            left: this._left,
            state: this._currentState,
            balanceLower: this._autoplaySettings.balanceLower,
            balanceLowerEnabled: this._autoplaySettings.balanceLowerEnabled,
            autoPlayBalance: this._autoPlayBalance,
        })

        if (
            this._currentState === GameStatesEnum.IDLE_STATE ||
            this._currentState === GameStatesEnum.LINE_BY_LINE_STATE
        ) {
            GameSignals.spinButtonClicked.emit()
        }
    }

    /**
     * Settings expresses both limits as a multiple of the stake, but stores the amount it resolved
     * at that moment. Auto Spins started from the in-game button reuse those stored preferences, so
     * an amount from an earlier stake would be tripped on the first spin after the player bets more.
     * Re-derive from the multiplier against the stake in force now, keeping the stored amount only
     * as a fallback for preferences saved before the multiplier was carried along.
     */
    private resolveStakeRelativeLimit = (
        multiplier: number | null | undefined,
        storedValue: number | null | undefined
    ): number | null | undefined => {
        const parsedMultiplier = Number(multiplier)
        if (!Number.isFinite(parsedMultiplier) || parsedMultiplier <= 0) {
            return storedValue
        }

        const currentBet = toMajorUnits(Number(this._model?.currentBet))
        if (!Number.isFinite(currentBet) || currentBet <= 0) {
            return storedValue
        }

        return Math.round(parsedMultiplier * currentBet * 100) / 100
    }

    private applyPreferredStopSettingsFromModel = () => {
        const preferred = (this._model as any)?.preferredAutoplayStopSettings as
            | PreferredAutoplayStopSettings
            | undefined
        if (!preferred) {
            return
        }

        this.onSetAutoplaySettings({
            stopOnAnyWin: !!preferred.stopOnAnyWin,
            singleWin: preferred.stopOnWinLimit
                ? this.resolveStakeRelativeLimit(preferred.stopWinMultiplier, preferred.stopSingleWinValue)
                : null,
            balanceLowerEnabled: !!preferred.stopOnLossLimit,
            balanceLower: preferred.stopOnLossLimit
                ? this.resolveStakeRelativeLimit(preferred.stopLossMultiplier, preferred.stopLossValue)
                : null,
        } as AutoplaySettings)
    }

    private onSetAutoplaySettings = (settings: AutoplaySettings) => {
        const parseLimit = (value: unknown): number | undefined => {
            if (value === null || value === undefined || value === '') return undefined
            const parsed = Number(value)
            if (!Number.isFinite(parsed) || parsed < 0) return undefined
            return parsed
        }

        if ('singleWin' in settings) {
            this._autoplaySettings.singleWin = parseLimit(settings.singleWin)
        }

        if ('balanceLower' in settings) {
            this._autoplaySettings.balanceLower = parseLimit(settings.balanceLower)
        }

        if ('balanceHigher' in settings) {
            this._autoplaySettings.balanceHigher = parseLimit(settings.balanceHigher)
        }

        if ('balanceLowerEnabled' in settings && settings.balanceLowerEnabled !== undefined) {
            this._autoplaySettings.balanceLowerEnabled = !!settings.balanceLowerEnabled
        }

        if ('balanceHigherEnabled' in settings && settings.balanceHigherEnabled !== undefined) {
            this._autoplaySettings.balanceHigherEnabled = !!settings.balanceHigherEnabled
        }

        if ('stopOnAnyWin' in settings && settings.stopOnAnyWin !== undefined) {
            this._autoplaySettings.stopOnAnyWin = !!settings.stopOnAnyWin
        }
    }

    private onStopAutoplay = () => {
        console.log(`[AutoplayManager#${this._instanceId}] onStopAutoplay`, {
            leftBefore: this._left,
            totalBefore: this._total,
            state: this._currentState,
        })
        Facade.isAuto = false
        this._hasProcessedSpinSignalForCurrentRound = false

        this._total = 0
        this._left = 0
    }

    private onFeatureTriggered = () => {
        if (!Facade.isAuto && this._left <= 0 && this._total <= 0) {
            return
        }

        GameSignals.stopAutoplay.emit()
        this.resetSettings()
    }

    private getCurrentSessionBalance = (): number => {
        const spinBalance = Number(this._model?.spinData?.balance)
        if (Number.isFinite(spinBalance)) {
            return spinBalance
        }

        const modelBalance = Number(this._model?.balance)
        return Number.isFinite(modelBalance) ? modelBalance : 0
    }

    private checkConditions(): boolean {
        if (!this._model.spinData) {
            return false
        }

        if (
            this._autoplaySettings.balanceHigherEnabled !== false &&
            this._autoplaySettings.balanceHigher !== undefined &&
            Number.isFinite(this._autoPlayBalance) &&
            this._model.spinData.balance >
                this._autoPlayBalance + (this._autoPlayBalance * this._autoplaySettings.balanceHigher) / 100
        ) {
            return true
        }

        // Session loss limit: absolute currency amount from Settings (same units as model.balance).
        if (
            this._autoplaySettings.balanceLowerEnabled !== false &&
            this._autoplaySettings.balanceLower !== undefined &&
            Number.isFinite(this._autoplaySettings.balanceLower) &&
            this._autoplaySettings.balanceLower > 0 &&
            Number.isFinite(this._autoPlayBalance)
        ) {
            const sessionLoss = this._autoPlayBalance - this.getCurrentSessionBalance()
            if (sessionLoss >= this._autoplaySettings.balanceLower) {
                return true
            }
        }

        if (this._autoplaySettings.stopOnAnyWin && this._model.spinData.win_currency > 0) {
            return true
        }

        // Settings limit is major units ($4.00); win_currency is minor (75 = $0.75).
        if (
            this._autoplaySettings.singleWin !== undefined &&
            this._autoplaySettings.singleWin > 0 &&
            toMajorUnits(this._model.spinData.win_currency) >= this._autoplaySettings.singleWin
        ) {
            return true
        }

        return false
    }

    private resetSettings() {
        // Only clear runtime autoplay counters. Keep Settings toggles
        // (stopOnAnyWin, loss/win limits, etc.) — the in-game Auto Spin button
        // restarts via startAutoplay without re-sending those preferences, so
        // wiping them here made "Stop on Any Win" work only on the first run.
        this._autoPlayBalance = undefined
        this._left = 0
    }
}
